import React, { useEffect, useState, useCallback } from 'react';
import { auth } from '../firebase';

const API = process.env.REACT_APP_API_URL || '';

async function refreshSession() {
  const user = auth.currentUser;
  if (!user) return false;
  try {
    const idToken = await user.getIdToken(true);
    const r = await fetch(`${API}/api/auth/session-login`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ idToken }),
    });
    return r.ok;
  } catch {
    return false;
  }
}
const PINK  = '#FF1F6A';
const BG    = '#080808';
const CARD  = '#111';
const BORDER= 'rgba(255,255,255,0.07)';
const WHITE = '#F0F0F0';
const MUTED = '#888';

const PRODUCT_LABELS = {
  monthly:          'Vybme+ Mensuel',
  monthly_renewal:  'Vybme+ Renouvellement',
  boost_1d:         'Boost 24h',
  boost_7d:         'Boost 7 jours',
  boost_30d:        'Boost 30 jours',
  superlikes_5:     'Super Likes ×5',
  superlikes_20:    'Super Likes ×20',
};

function fmt(cents) {
  return `${(cents / 100).toFixed(2)} $`;
}

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function VibeeRevenueSection() {
  const [stats,   setStats]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [filter,  setFilter]  = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let resp = await fetch(`${API}/api/vibee-admin/stats`, { credentials: 'include' });
      if (resp.status === 401) {
        const refreshed = await refreshSession();
        if (!refreshed) throw new Error('Session expirée — veuillez vous reconnecter');
        resp = await fetch(`${API}/api/vibee-admin/stats`, { credentials: 'include' });
      }
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setStats(await resp.json());
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filteredOrders = stats?.recentOrders?.filter(o => {
    if (filter === 'all') return true;
    if (filter === 'subscription') return o.product === 'monthly' || o.product === 'monthly_renewal';
    if (filter === 'boost') return o.product?.startsWith('boost');
    if (filter === 'superlikes') return o.product?.startsWith('superlikes');
    return true;
  }) || [];

  return (
    <div style={{ padding: '28px 32px', maxWidth: 1100, color: WHITE }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
        <div>
          <h2 style={{ fontSize: '1.4rem', fontWeight: 800, margin: 0 }}>Revenus Vybme</h2>
          <p style={{ fontSize: '0.8rem', color: MUTED, margin: '4px 0 0' }}>Paiements Stripe en temps réel</p>
        </div>
        <button onClick={load} style={{ background: CARD, border: `1px solid ${BORDER}`, color: WHITE, borderRadius: 10, padding: '8px 16px', cursor: 'pointer', fontSize: '0.8rem', fontFamily: 'inherit' }}>
          ↻ Actualiser
        </button>
      </div>

      {error && (
        <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 12, padding: '14px 18px', marginBottom: 24, color: '#FCA5A5', fontSize: '0.85rem' }}>
          Erreur : {error}
        </div>
      )}

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14, marginBottom: 28 }}>
        {[
          { label: 'Revenus totaux',     value: stats ? fmt(stats.totalRevenue)        : '—', icon: '💰', color: '#22C55E' },
          { label: 'Revenus ce mois',    value: stats ? fmt(stats.monthRevenue)         : '—', icon: '📅', color: PINK      },
          { label: 'Commandes totales',  value: stats ? stats.totalOrders              : '—', icon: '🧾', color: '#A855F7' },
          { label: 'Commandes ce mois',  value: stats ? stats.monthOrders              : '—', icon: '📈', color: '#F59E0B' },
          { label: 'Abonnés actifs',     value: stats ? stats.activeSubscriptions      : '—', icon: '💎', color: PINK      },
        ].map(k => (
          <div key={k.label} style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 14, padding: '18px 20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <span style={{ fontSize: '1.4rem' }}>{k.icon}</span>
              <p style={{ fontSize: '0.72rem', color: MUTED, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{k.label}</p>
            </div>
            <p style={{ fontSize: '1.6rem', fontWeight: 900, color: loading ? MUTED : k.color, transition: 'color 0.3s' }}>
              {loading ? '…' : k.value}
            </p>
          </div>
        ))}
      </div>

      {/* Filtres */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {[['all','Tout'],['subscription','Abonnements'],['boost','Boosts'],['superlikes','Super Likes']].map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)}
            style={{ padding: '6px 14px', borderRadius: 20, border: `1px solid ${filter === v ? PINK : BORDER}`, background: filter === v ? 'rgba(255,31,106,0.12)' : 'transparent', color: filter === v ? PINK : MUTED, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s' }}>
            {l}
          </button>
        ))}
      </div>

      {/* Tableau */}
      <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 16, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr 0.8fr 0.6fr', padding: '12px 20px', borderBottom: `1px solid ${BORDER}`, fontSize: '0.72rem', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          <span>Date</span>
          <span>Produit</span>
          <span>Montant</span>
          <span>UID</span>
        </div>

        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: MUTED, fontSize: '0.85rem' }}>Chargement…</div>
        ) : filteredOrders.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: MUTED, fontSize: '0.85rem' }}>Aucune commande</div>
        ) : filteredOrders.map((o, i) => (
          <div key={o.id} style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr 0.8fr 0.6fr', padding: '13px 20px', borderBottom: i < filteredOrders.length - 1 ? `1px solid ${BORDER}` : 'none', alignItems: 'center', fontSize: '0.82rem' }}>
            <span style={{ color: MUTED }}>{fmtDate(o.at)}</span>
            <span style={{ fontWeight: 600, color: WHITE }}>
              {PRODUCT_LABELS[o.product] || o.productName || o.product}
            </span>
            <span style={{ fontWeight: 800, color: '#22C55E' }}>{fmt(o.amount)}</span>
            <span style={{ color: MUTED, fontSize: '0.7rem', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={o.uid}>{o.uid?.slice(0, 8)}…</span>
          </div>
        ))}
      </div>

      {stats && (
        <p style={{ fontSize: '0.72rem', color: MUTED, marginTop: 12, textAlign: 'right' }}>
          {filteredOrders.length} commande{filteredOrders.length > 1 ? 's' : ''} affichée{filteredOrders.length > 1 ? 's' : ''}
        </p>
      )}
    </div>
  );
}
